import { useCallback, useEffect, useRef, useState } from 'react';
import {
  developerIdSchema,
  modelFilterSchema,
  pricedToolSchema,
  projectFilterSchema,
  surfaceFilterSchema,
  toolSchema,
  type PricedTool,
  type Tool,
} from '@ace-tracker/shared';
import { presetToRange, RANGE_PRESETS, type RangePreset } from './ranges';

/**
 * A view is the leadership Executive summary, the cross-tool "All tools"
 * overview (see dashboard/AGENTS.md "API rules"), one of the three per-tool
 * tabs, or the admin Settings page.
 */
export type ViewMode = Tool | 'executive' | 'overview' | 'spend' | 'efficiency' | 'sessions' | 'optimization' | 'rtk' | 'headroom' | 'graphify' | 'health' | 'settings';

const NON_TOOL_VIEWS: readonly string[] = ['executive', 'overview', 'spend', 'efficiency', 'sessions', 'optimization', 'rtk', 'headroom', 'graphify', 'health', 'settings'];

/** The cost views share the tool, model, project, and surface filters. */
export const COST_VIEWS: readonly ViewMode[] = ['spend', 'efficiency', 'sessions'];

export interface CostFilters {
  tools: PricedTool[];
  models: string[];
  projects: string[];
  surfaces: string[];
}

export const NO_COST_FILTERS: CostFilters = { tools: [], models: [], projects: [], surfaces: [] };

export function hasCostFilters(filters: CostFilters): boolean {
  return Object.values(filters).some((values) => values.length > 0);
}

export function isToolView(view: ViewMode): view is Tool {
  return !NON_TOOL_VIEWS.includes(view);
}

export interface DashboardUrlState {
  view: ViewMode;
  preset: RangePreset;
  /** Only meaningful when preset is 'C'; ISO timestamps like presetToRange returns. */
  customRange: { from: string; to: string };
  /** Developer id, or a team selection encoded as `team:<name>`. */
  developerId?: string;
  /** Spend, Efficiency and Sessions only; kept while moving between those views. */
  filters: CostFilters;
}

export const DEFAULT_VIEW: ViewMode = 'executive';
const DEFAULT_PRESET: RangePreset = 'M';

/** Per-tool and optimizer tabs open on the last week; every other view on the last month. */
function defaultPreset(view: ViewMode): RangePreset {
  return isToolView(view) || view === 'optimization' || view === 'rtk' || view === 'headroom' || view === 'graphify' ? 'W' : DEFAULT_PRESET;
}
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parses the query string into dashboard state. Every value is validated
 * (the same shared zod schemas the API uses) and anything unrecognised falls
 * back to the default, so a hand-edited or stale link never breaks the page.
 */
function readFromUrl(): DashboardUrlState {
  const params = new URLSearchParams(window.location.search);

  const rawView = params.get('view');
  const view: ViewMode =
    rawView && NON_TOOL_VIEWS.includes(rawView) ? (rawView as ViewMode) : (toolSchema.safeParse(rawView).data ?? DEFAULT_VIEW);

  const rawRange = params.get('range');
  const preset = RANGE_PRESETS.find((p) => p.key === rawRange)?.key ?? defaultPreset(view);

  const fallback = presetToRange('M');
  const from = params.get('from');
  const to = params.get('to');
  const customRange = {
    from: from && DATE_ONLY.test(from) ? new Date(from).toISOString() : fallback.from,
    to: to && DATE_ONLY.test(to) ? new Date(to).toISOString() : fallback.to,
  };

  // Developer identifiers are scoped to one telemetry family, including the optimization family.
  const rawDeveloper = params.get('dev');
  const developerId = (isToolView(view) || view === 'optimization' || view === 'rtk' || view === 'headroom' || view === 'graphify')
    ? (rawDeveloper?.startsWith('team:') && rawDeveloper.length <= 105
      ? rawDeveloper
      : developerIdSchema.safeParse(rawDeveloper).data)
    : undefined;

  return { view, preset, customRange, developerId, filters: readFilters(params) };
}

/** Each filter value is validated with the API's own schema; anything else is dropped. */
function readFilters(params: URLSearchParams): CostFilters {
  const valid = <T>(key: string, schema: { safeParse: (v: unknown) => { success: boolean; data?: T } }, max: number): T[] =>
    [...new Set(params.getAll(key))]
      .map((value) => schema.safeParse(value))
      .filter((result) => result.success)
      .map((result) => result.data as T)
      .slice(0, max);
  return {
    tools: valid('tools', pricedToolSchema, 2),
    models: valid('models', modelFilterSchema, 50),
    projects: valid('projects', projectFilterSchema, 50),
    surfaces: valid('surfaces', surfaceFilterSchema, 20),
  };
}

function toSearch(state: DashboardUrlState): string {
  const params = new URLSearchParams();
  if (state.view !== DEFAULT_VIEW) params.set('view', state.view);
  if (state.preset !== defaultPreset(state.view)) params.set('range', state.preset);
  if (state.preset === 'C') {
    params.set('from', state.customRange.from.slice(0, 10));
    params.set('to', state.customRange.to.slice(0, 10));
  }
  if (state.developerId) params.set('dev', state.developerId);
  if (COST_VIEWS.includes(state.view)) {
    for (const [key, values] of Object.entries(state.filters)) for (const value of values) params.append(key, value);
  }
  const search = params.toString();
  return search ? `?${search}` : '';
}

/**
 * Dashboard view + filter state mirrored into the URL, so a view can be
 * bookmarked or shared and the browser Back button moves between views.
 * Switching view pushes a history entry; changing a filter replaces the
 * current one (so Back doesn't step through every range click).
 */
export function useDashboardUrlState() {
  const [state, setState] = useState<DashboardUrlState>(readFromUrl);
  // Latest state for `update`, which touches history and so must not run
  // inside a setState updater (StrictMode double-invokes those).
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    const onPopState = () => setState(readFromUrl());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const update = useCallback((patch: Partial<DashboardUrlState>, options?: { replace?: boolean }) => {
    const prev = stateRef.current;
    const next = { ...prev, ...patch };
    // An untouched range follows the new view's default.
    if (patch.view !== undefined && patch.preset === undefined && prev.preset === defaultPreset(prev.view)) {
      next.preset = defaultPreset(patch.view);
    }
    const url = `${window.location.pathname}${toSearch(next)}${window.location.hash}`;
    if (patch.view !== undefined && patch.view !== prev.view && !options?.replace) window.history.pushState(null, '', url);
    else window.history.replaceState(null, '', url);
    stateRef.current = next;
    setState(next);
  }, []);

  return [state, update] as const;
}
